import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faPen, faTrash, faUsers, faEnvelope, faMapMarkerAlt, faStar, faEllipsisV } from '@fortawesome/free-solid-svg-icons';
import ConfirmModal from '../../../common/ConfirmModal';
import NextOfKinModal from './NextOfKinModal';
import PhoneActions from './PhoneActions';
import { relationshipLabel } from './nextOfKinHelpers';
const { rootUrl } = require('../../../../config/config');

// Edit/remove menu, kept behind a ⋮ so the destructive action isn't next to the user's thumb
const RowMenu = ({ onEdit, onRemove }) => {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        if (!open) return undefined;
        const onDocClick = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('click', onDocClick);
        return () => document.removeEventListener('click', onDocClick);
    }, [open]);

    const choose = (action) => { setOpen(false); action(); };

    return (
        <div className="relative shrink-0" ref={ref}>
            <button
                onClick={() => setOpen((o) => !o)}
                aria-label="Contact actions"
                aria-expanded={open}
                className="w-9 h-9 rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-700 flex items-center justify-center"
            >
                <FontAwesomeIcon icon={faEllipsisV} />
            </button>
            {open && (
                <div className="absolute right-0 mt-1 w-36 bg-white border border-gray-200 rounded-lg shadow-lg z-20 py-1">
                    <button onClick={() => choose(onEdit)} className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2">
                        <FontAwesomeIcon icon={faPen} className="text-gray-400" />Edit
                    </button>
                    <button onClick={() => choose(onRemove)} className="w-full text-left px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2">
                        <FontAwesomeIcon icon={faTrash} />Remove
                    </button>
                </div>
            )}
        </div>
    );
};

const DriverNextOfKin = ({ driver, onContactsChange }) => {
    const [contacts, setContacts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [modalOpen, setModalOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [deleting, setDeleting] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [deleteError, setDeleteError] = useState('');

    const token = localStorage.getItem('authToken') || '';
    const baseUrl = `${rootUrl.replace(/\/$/, '')}/drivers/${driver.id}/next-of-kin`;

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const res = await fetch(baseUrl, { headers: { Authorization: `Bearer ${token}` } });
            const data = await res.json();
            if (!res.ok || !data.success) throw new Error(data.message || 'Failed to load next of kin');
            setContacts(data.data || []);
            if (onContactsChange) onContactsChange(data.data || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [baseUrl, token, onContactsChange]);

    useEffect(() => { load(); }, [load]);

    const openAdd = () => { setEditing(null); setModalOpen(true); };
    const openEdit = (contact) => { setEditing(contact); setModalOpen(true); };

    const confirmDelete = async () => {
        setDeleteLoading(true);
        setDeleteError('');
        try {
            const res = await fetch(`${baseUrl}/${deleting.id}`, {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${token}` }
            });
            const data = await res.json();
            if (!res.ok || !data.success) throw new Error(data.message || 'Failed to remove next of kin');
            setDeleting(null);
            load();
        } catch (err) {
            setDeleteError(err.message);
        } finally {
            setDeleteLoading(false);
        }
    };

    return (
        <div>
            <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-[#2a2e30]">
                    Next of Kin{!loading && <span className="text-gray-400 font-normal"> ({contacts.length})</span>}
                </h3>
                <button
                    onClick={openAdd}
                    className="px-3 py-2 bg-[#FF5733] text-white text-sm font-medium rounded-lg hover:bg-[#E64A19] transition-colors flex items-center gap-2"
                >
                    <FontAwesomeIcon icon={faPlus} />
                    Add
                </button>
            </div>

            {error && <p className="text-red-500 text-sm mb-4">{error}</p>}

            {loading ? (
                <div className="flex justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF5733]"></div>
                </div>
            ) : contacts.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 rounded-lg border border-dashed border-gray-300 text-gray-500">
                    <FontAwesomeIcon icon={faUsers} className="text-3xl mb-3 text-gray-300" />
                    <p>No next of kin recorded for this driver yet.</p>
                    <p className="text-xs mt-1">A next of kin is required before this driver can be assigned a vehicle.</p>
                </div>
            ) : (
                <ul className="bg-white rounded-2xl border border-gray-200 divide-y divide-gray-100">
                    {contacts.map((c) => {
                        const meta = [relationshipLabel(c.relationship), c.id_no && `ID ${c.id_no}`, c.is_guarantor && 'Guarantor'].filter(Boolean);
                        return (
                            <li key={c.id} className="px-4 py-4 flex gap-2">
                                <div className="flex-1 min-w-0 md:grid md:grid-cols-2 md:gap-8">
                                    <div className="min-w-0">
                                        <div className="flex items-center gap-2">
                                            <span className="font-semibold text-[#2a2e30] truncate">{c.name}</span>
                                            {c.is_primary && (
                                                <span className="shrink-0 text-xs font-medium text-[#FF5733]">
                                                    <FontAwesomeIcon icon={faStar} className="mr-1" />Primary
                                                </span>
                                            )}
                                        </div>
                                        <p className="text-xs text-gray-500 mt-0.5">{meta.join(' · ')}</p>
                                        {(c.email || c.physical_address || c.notes) && (
                                            <div className="mt-1 space-y-0.5 text-xs text-gray-500">
                                                {c.physical_address && <p className="truncate"><FontAwesomeIcon icon={faMapMarkerAlt} className="mr-1.5 text-gray-400" />{c.physical_address}</p>}
                                                {c.email && <p className="truncate"><FontAwesomeIcon icon={faEnvelope} className="mr-1.5 text-gray-400" />{c.email}</p>}
                                                {c.notes && <p className="italic truncate" title={c.notes}>{c.notes}</p>}
                                            </div>
                                        )}
                                    </div>
                                    <div className="mt-3 md:mt-0 space-y-2">
                                        <PhoneActions phone={c.phone} label={c.name} />
                                        <PhoneActions phone={c.alt_phone} label={`${c.name} (alternative)`} tag="Alt" />
                                    </div>
                                </div>
                                <RowMenu
                                    onEdit={() => openEdit(c)}
                                    onRemove={() => { setDeleteError(''); setDeleting(c); }}
                                />
                            </li>
                        );
                    })}
                </ul>
            )}

            <NextOfKinModal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                driver={driver}
                existing={editing}
                isFirst={!editing && contacts.length === 0}
                onSaved={load}
            />

            <ConfirmModal
                isOpen={!!deleting}
                title="Remove Next of Kin"
                description={deleting ? `Remove ${deleting.name} (${relationshipLabel(deleting.relationship)}) as next of kin for ${driver.name}?${deleting.is_primary && contacts.length > 1 ? ' The oldest remaining contact will become primary.' : ''}` : ''}
                onCancel={() => setDeleting(null)}
                onConfirm={confirmDelete}
                confirmText="Remove"
                loading={deleteLoading}
                error={deleteError}
                variant="danger"
            />
        </div>
    );
};

export default DriverNextOfKin;
